import { useTranslation } from 'react-i18next'
import { Clapperboard, Film } from 'lucide-react'
import { forwardRef, type Ref } from 'react'

import type { ReviewQueueItem } from '@/api/studio-production'
import { MediaKindThumb } from '@/features/studio/components/MediaKindThumb'
import { inferMediaKind, resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import {
  ReviewVideoPlayer,
  type ReviewCommentMarker,
  type ReviewVideoPlayerHandle,
} from '@/features/studio/pages/reviews/ReviewVideoPlayer'

export function reviewMediaKind(item: Pick<ReviewQueueItem, 'preview_url' | 'preview_kind'>): 'image' | 'video' | 'audio' | 'other' {
  return inferMediaKind(item.preview_kind, item.preview_url)
}

export function ReviewQueueThumb({ item }: { item: ReviewQueueItem }) {
  const { t } = useTranslation()
  const kind = reviewMediaKind(item)

  if (kind === 'video' && item.preview_url) {
    return (
      <div className="relative h-full w-full bg-black">
        <MediaKindThumb url={item.preview_url} mediaKind={item.preview_kind} className="h-full w-full" />
        <span className="absolute bottom-1 right-1 inline-flex items-center gap-0.5 rounded bg-black/70 px-1 py-0.5 text-[8px] text-zinc-200">
          <Clapperboard className="h-2.5 w-2.5" />
          {t('reviews.videoBadge')}
        </span>
      </div>
    )
  }

  return <MediaKindThumb url={item.preview_url} mediaKind={item.preview_kind} />
}

type ReviewMediaPreviewProps = {
  item: ReviewQueueItem
  videoRef?: Ref<ReviewVideoPlayerHandle>
  commentMarkers?: ReviewCommentMarker[]
  onCommentMarkerClick?: (marker: ReviewCommentMarker) => void
  onInOutChange?: (range: { in: number | null; out: number | null }) => void
}

export const ReviewMediaPreview = forwardRef<ReviewVideoPlayerHandle, ReviewMediaPreviewProps>(
  function ReviewMediaPreview({ item, videoRef, commentMarkers, onCommentMarkerClick, onInOutChange }, ref) {
    const { t } = useTranslation()
    const url = item.preview_url
    const kind = reviewMediaKind(item)
    const playerRef = videoRef ?? ref

    if (!url?.trim()) {
      return (
        <div className="review-media-preview flex flex-col items-center gap-3 text-zinc-500">
          <Film className="h-12 w-12 text-zinc-600" />
          <p className="text-sm">{t('reviews.noPreview')}</p>
        </div>
      )
    }

    if (kind === 'video') {
      return (
        <ReviewVideoPlayer
          ref={playerRef}
          src={url}
          durationHintSec={item.duration_sec}
          markers={commentMarkers}
          onMarkerClick={onCommentMarkerClick}
          onInOutChange={onInOutChange}
          className="review-media-preview"
        />
      )
    }

    if (kind === 'audio') {
      return (
        <audio
          src={resolveMediaDisplayUrl(url)}
          controls
          className="review-media-preview w-full max-w-lg rounded-lg bg-card px-3 py-2 ring-1 ring-border"
        />
      )
    }

    return (
      <img
        src={resolveMediaDisplayUrl(url)}
        alt=""
        className="review-media-preview max-h-full max-w-full rounded-lg object-contain shadow-lg ring-1 ring-white/10"
      />
    )
  },
)
